import { useFetchRecordCount } from '@modules/common/hooks';
import { mapToDateTimeRange } from '@modules/common/utils';
import {
  useGetQuantifyCompareQuery,
  useGetQuantifyWhereClause,
} from '@modules/quantify/hooks';
import { useGetQuantifyDuration } from '@modules/quantify/hooks/useGetQuantifyDuration';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import {
  getDurationForCompareByPreviousTimePeriod,
  getHasMetadataFilterForEqlField,
} from '@modules/quantify/utils';
import { TimeUnit } from 'generated/graphql';
import { useMemo } from 'react';
import { TimeRange } from 'v2/core/entities';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import {
  EmptyWhereClause,
  OperationType,
} from 'v2/core/services/queryBuilder/whereClause';

interface FetchTotalRecordCountForCompareFieldsOptions {
  shouldSkip?: boolean;
}

export const useFetchTotalRecordCountForCompareFields = ({
  shouldSkip = false,
}: FetchTotalRecordCountForCompareFieldsOptions = {}) => {
  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const compareQuery = useGetQuantifyCompareQuery();
  const durationA = useGetQuantifyDuration();

  const whereClauseA = useGetQuantifyWhereClause();

  const metadataFilter = getHasMetadataFilterForEqlField(groupBy);

  const combinedWhereClauseA = metadataFilter
    ? combineWhereClauses(whereClauseA, metadataFilter, OperationType.AND)
    : whereClauseA;

  const queryContainerForFieldA: QueryContainer = {
    AggregatePeriod: TimeUnit.Week,
    DateRange: durationA as TimeRange,
    WhereClause: combinedWhereClauseA,
  };

  const { data: recordCountA, isLoading: isLoadingRecordCountA } =
    useFetchRecordCount({
      queryContainer: queryContainerForFieldA,
      skip: shouldSkip || !compareQuery,
    });

  const getQueryContainerForFieldB = (): QueryContainer => {
    if (compareQuery?.whereClause) {
      const compareWhereClause = compareQuery.whereClause ?? EmptyWhereClause;

      return {
        AggregatePeriod: TimeUnit.Week,
        DateRange: durationA as TimeRange,
        WhereClause: metadataFilter
          ? combineWhereClauses(
              compareWhereClause ?? EmptyWhereClause,
              metadataFilter,
              OperationType.AND,
            )
          : compareWhereClause ?? EmptyWhereClause,
      };
    }

    if (compareQuery?.timerange) {
      return {
        AggregatePeriod: TimeUnit.Week,
        DateRange: mapToDateTimeRange(compareQuery.timerange) as TimeRange,
        WhereClause: combinedWhereClauseA,
      };
    }

    if (compareQuery?.previousPeriod) {
      return {
        AggregatePeriod: TimeUnit.Week,
        DateRange: getDurationForCompareByPreviousTimePeriod(
          durationA,
          compareQuery,
        ) as TimeRange,
        WhereClause: combinedWhereClauseA,
      };
    }

    return queryContainerForFieldA;
  };

  const { data: recordCountB, isLoading: isLoadingRecordCountB } =
    useFetchRecordCount({
      queryContainer: getQueryContainerForFieldB(),
      skip: shouldSkip || !compareQuery,
    });

  const totalFeedbackCount = useMemo(() => {
    if (recordCountA !== undefined && recordCountB !== undefined) {
      return {
        fieldA: recordCountA,
        fieldB: recordCountB,
      };
    }
    return undefined;
  }, [recordCountA, recordCountB]);

  return {
    totalFeedbackCount,
    isLoading: isLoadingRecordCountA || isLoadingRecordCountB,
  };
};
